import { useApolloClient, useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ManufacturerProductPropertyOptionDetail } from '@deltek/specification-client/core/Models/product'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { arrayMove } from '@dnd-kit/sortable'
import {
    Backdrop,
    CircularProgress,
    IconButton,
    Theme,
    makeStyles,
} from '@material-ui/core'
import { AddOutlined } from '@material-ui/icons'
import { GetIsManufacturerOptionDetailsRemovable } from 'bpm/api/graphQL/queries/product.queries'
import React, { FC, useCallback, useContext, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import colors from '../../../listingColors'
import {
    ProductContext,
    ProductContextType,
    SupportedOptionsObject,
} from '../../ListingDetailsPage'
import Option from '../Option'

import { stripDoubleSpaces } from 'bpm/shared/utils/inputUtils'
import { insertToIndex } from './NonMasterSpec/nonMSReducer'
import { PPL_ACTIONS, usePPLContext } from './PPLProvider'
import DndProperties from './components/DndProperties'
import { ERROR_CANNOT_REMOVE_OPTION, MANUFACTURER_OPTION } from './constants'
import { checkForDuplicates } from './utils/optionUtils'

const {
    defaultLight,
    addOptionFillButton,
    addOptionButtonHover,
    addOptionButtonSecondary,
} = colors

const useStyles = makeStyles((theme: Theme) => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
    addPremiumPlusProperty: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: pxToRem(5),
        gap: pxToRem(10),
        width: pxToRem(24),
        height: pxToRem(24),
        marginRight: theme.spacing(2),
        color: defaultLight,
        backgroundColor: addOptionFillButton,
        border: `1px solid ${addOptionButtonSecondary}`,
        borderRadius: pxToRem(100),
        '&:hover': {
            color: defaultLight,
            backgroundColor: addOptionButtonHover,
            border: `1px solid ${addOptionButtonSecondary}`,
            cursor: 'pointer',
        },
    },
    addIcon: {
        width: pxToRem(20),
        height: pxToRem(20),
        fill: addOptionButtonSecondary,
        '&:hover': {
            fill: addOptionButtonSecondary,
        },
    },
    premPlusContainer: {
        height: 'auto',
        alignItems: 'center',
        paddingTop: theme.spacing(1),
    },
}))

type PremiumProductPropertiesType = Record<
    string,
    Record<
        string,
        Record<string, Array<Partial<ManufacturerProductPropertyOptionDetail>>>
    >
>
interface MSPremiumPropertiesProps {
    propertyName: string
    subPropertyName: string
    groupId: string
    propertyId: string
}

type DndOptionValue =
    | string
    | Partial<ManufacturerProductPropertyOptionDetail>
    | Array<Partial<ManufacturerProductPropertyOptionDetail>>
    | PremiumProductPropertiesType

const MSPremiumProperties: FC<MSPremiumPropertiesProps> = ({
    propertyName,
    subPropertyName,
    groupId,
    propertyId,
}) => {
    const classes = useStyles()
    const client = useApolloClient()

    const [isAddActive, setIsAddActive] = useState(false)

    const {
        msProperties: { selectedOptions, properties, pPlusItems },
        dispatch,
    } = usePPLContext()

    const [isRemoveLoading, setIsRemoveLoading] = useState(false)

    const { isManufacturerOptionsReadOnly, product } = useContext<
        Partial<ProductContextType>
    >(ProductContext)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const premiumOptions = useMemo(
        () => pPlusItems[propertyName][subPropertyName][groupId] ?? [],
        [propertyName, subPropertyName, pPlusItems, groupId]
    )

    const setSelectedData = useCallback(
        (value: string, selectedArray: Array<string> = []) =>
            selectedArray?.includes(value)
                ? selectedArray?.filter((d) => d !== value)
                : [...selectedArray, value],
        []
    )

    const setPremiumOptions = useCallback(
        (
            value: Record<string, string | boolean>,
            premiumOptionArray?: Array<Record<string, string | boolean>>
        ) => {
            return [...(premiumOptionArray || []), value]
        },
        []
    )

    const editPremiumOption = useCallback(
        (
            value: Record<string, string | boolean>,
            premiumOptionArray?: Array<Record<string, string | boolean>>
        ) => {
            const optionIndex =
                premiumOptionArray?.findIndex(
                    (option) => option.optionId === value.optionId
                ) ?? 0

            return insertToIndex(
                premiumOptionArray ?? [],
                optionIndex,
                value,
                true
            )
        },
        [insertToIndex]
    )

    const removePremiumOption = useCallback(
        (
            id: string,
            premiumOptionArray: Array<Record<string, string | boolean>>
        ) => premiumOptionArray.filter((option) => option.optionId !== id),
        []
    )

    const processPropertiesData = (
        propertyName: string,
        subPropertyName: string,
        value: DndOptionValue,
        rawObject: PremiumProductPropertiesType = {},
        processFn: (...props: any) => any, //explicitly set as any for reusability
        groupId: string
    ) => {
        if (!!subPropertyName && !!groupId && !!value) {
            return {
                ...rawObject,
                [propertyName]: {
                    ...(rawObject[propertyName] ?? {}),
                    [subPropertyName!]: {
                        ...(rawObject[propertyName][subPropertyName] ?? {}),
                        [groupId!]: processFn(
                            value,
                            rawObject[propertyName][subPropertyName][groupId]
                        ),
                    },
                },
            }
        }
        return rawObject
    }

    const processSelectionData = (
        propertyName: string,
        subPropertyName: string,
        value: DndOptionValue,
        rawObject: PremiumProductPropertiesType = {},
        processFn: (...props: any) => any
    ) => {
        if (!!subPropertyName && !!groupId && !!value) {
            return {
                ...rawObject,
                [propertyName]: {
                    ...(rawObject[propertyName] ?? {}),
                    [subPropertyName!]: processFn(
                        value,
                        rawObject[propertyName][subPropertyName]
                    ),
                },
            }
        }
        return rawObject
    }

    const insertPremiumOption = useCallback(
        (property) => {
            if (property) {
                const assignedProperty =
                    typeof property === 'string'
                        ? property
                        : `${property.imperialUnits} (${property.metricUnits})`

                if (properties) {
                    // check if there are duplicate on masterspec docs
                    checkForDuplicates({
                        property,
                        properties,
                        premiumOptions,
                        propertyName,
                        subPropertyName,
                        isMasterSpec: true,
                    })
                }

                const optionId = uuidv4()

                let valueFragment = {
                    isUnitOfMeasure: false,
                    imperialUnits: '',
                    metricUnits: '',
                }

                if (typeof property !== 'string') {
                    valueFragment = {
                        isUnitOfMeasure: true,
                        imperialUnits: property.imperialUnits ?? '',
                        metricUnits: property.metricUnits ?? '',
                    }
                }

                const newOption = {
                    value: assignedProperty,
                    isOther: false,
                    isPremiumPlus: true,
                    isPremium: true,
                    isSupported: true,
                    optionId,
                    ...valueFragment,
                }

                dispatch({
                    type: PPL_ACTIONS.batchMSActions,
                    payload: [
                        {
                            type: PPL_ACTIONS.setPPlusItems,
                            payload: processPropertiesData(
                                propertyName,
                                subPropertyName,
                                newOption,
                                pPlusItems,
                                setPremiumOptions,
                                groupId
                            ),
                        },
                        {
                            type: PPL_ACTIONS.setSelectedOptions,
                            payload: processSelectionData(
                                propertyName,
                                subPropertyName,
                                optionId,
                                selectedOptions,
                                setSelectedData
                            ) as SupportedOptionsObject,
                        },
                    ],
                })
            }

            setIsAddActive(false)
            return false
        },
        [
            propertyName,
            subPropertyName,
            premiumOptions,
            properties,
            selectedOptions,
            setIsAddActive,
            setPremiumOptions,
            setSelectedData,
            pPlusItems,
            dispatch,
            groupId,
        ]
    )

    const handleDragEnd = useCallback(
        (active, over) => {
            if (!!active && !!over && active.id !== over.id) {
                const oldItem = pPlusItems[propertyName][subPropertyName][
                    groupId
                ].findIndex(
                    (item: Partial<ManufacturerProductPropertyOptionDetail>) =>
                        item.optionId === active.id
                )

                const newItem = pPlusItems[propertyName][subPropertyName][
                    groupId
                ].findIndex(
                    (item: Partial<ManufacturerProductPropertyOptionDetail>) =>
                        item.optionId === over.id
                )

                dispatch({
                    type: PPL_ACTIONS.setPPlusItems,
                    payload: processPropertiesData(
                        propertyName,
                        subPropertyName,
                        arrayMove(
                            pPlusItems[propertyName][subPropertyName][groupId],
                            oldItem,
                            newItem
                        ) as Array<
                            ManufacturerProductPropertyOptionDetail & {
                                isPremiumPlus: boolean
                            }
                        >,
                        pPlusItems,
                        (value) => value,
                        groupId
                    ) as PremiumProductPropertiesType,
                })
            }
        },
        [dispatch, pPlusItems, propertyName, subPropertyName, groupId]
    )

    const handleRemoveClick = useCallback(
        async (id: string) => {
            const optionId = id
            const optionGroupId = groupId

            setIsRemoveLoading(true)
            try {
                const {
                    data: {
                        getManufacturerOptionDetails: {
                            manufacturerOptionGroupDetails,
                        },
                    },
                } = await client.query({
                    query: GetIsManufacturerOptionDetailsRemovable,
                    variables: {
                        input: {
                            listingId: product?.listingId ?? '',
                            details: [{ propertyId, optionGroupId, optionId }],
                        },
                    },
                })

                if (!!manufacturerOptionGroupDetails[0].isDetailsRemovable) {
                    dispatch({
                        type: PPL_ACTIONS.setPPlusItems,
                        payload: processPropertiesData(
                            propertyName,
                            subPropertyName,
                            id,
                            pPlusItems,
                            removePremiumOption,
                            groupId
                        ) as PremiumProductPropertiesType,
                    })
                } else {
                    throw new Error()
                }
            } catch (err) {
                showGlobalSnackbar({
                    variables: {
                        message: ERROR_CANNOT_REMOVE_OPTION,
                        variant: 'error',
                        id: 'RemoveOptionFailed',
                    },
                })
            } finally {
                setIsRemoveLoading(false)
            }
        },
        [
            dispatch,
            pPlusItems,
            propertyName,
            subPropertyName,
            removePremiumOption,
            groupId,
        ]
    )

    const handleSetSelected = useCallback(
        (id: string) => {
            const optionGroup =
                pPlusItems[propertyName][subPropertyName][groupId]
            const optionIndex = optionGroup.findIndex(
                (option: Partial<ManufacturerProductPropertyOptionDetail>) =>
                    option.optionId === id
            )
            const option = optionGroup[optionIndex]

            dispatch({
                type: PPL_ACTIONS.setSelectedOptions,
                payload: processSelectionData(
                    propertyName,
                    subPropertyName,
                    id,
                    selectedOptions,
                    setSelectedData
                ) as SupportedOptionsObject,
            })

            dispatch({
                type: PPL_ACTIONS.setPPlusItems,
                payload: processPropertiesData(
                    propertyName,
                    subPropertyName,
                    insertToIndex(
                        pPlusItems,
                        optionIndex,
                        { ...option, isSupported: !option.isSupported },
                        true
                    ),
                    pPlusItems,
                    (value) => value,
                    groupId
                ),
            })
        },
        [
            propertyName,
            subPropertyName,
            selectedOptions,
            dispatch,
            setSelectedData,
        ]
    )

    const handleAddPremPlusProperty = useCallback(() => {
        setIsAddActive(true)
    }, [setIsAddActive])

    const determineSelected = useCallback(
        (value, optionId) =>
            !!value &&
            !!selectedOptions &&
            selectedOptions[propertyName] &&
            selectedOptions[propertyName][subPropertyName].includes(
                optionId ?? ''
            ),
        [selectedOptions, propertyName, subPropertyName]
    )

    const dispatchEditPremiumOption = useCallback(
        (value) => {
            dispatch({
                type: PPL_ACTIONS.setPPlusItems,
                payload: processPropertiesData(
                    propertyName,
                    subPropertyName,
                    value,
                    pPlusItems,
                    editPremiumOption,
                    groupId
                ) as PremiumProductPropertiesType,
            })
        },
        [propertyName, subPropertyName, pPlusItems, groupId]
    )

    const onOptionBlur = useCallback(
        (id: string, value: string) => {
            dispatchEditPremiumOption({
                ...pPlusItems[propertyName][subPropertyName][groupId].filter(
                    (option: ManufacturerProductPropertyOptionDetail) =>
                        option.optionId === id
                )[0],
                value: stripDoubleSpaces(value),
            })
        },
        [propertyName, subPropertyName, pPlusItems, groupId, stripDoubleSpaces]
    )

    const onUOMBlur = useCallback(
        (property) => {
            dispatchEditPremiumOption(property)
        },
        [dispatchEditPremiumOption]
    )

    const viewOnlyPremiumOptions = () => {
        return premiumOptions.map(
            (option: ManufacturerProductPropertyOptionDetail) => {
                const isSelected = determineSelected(
                    option.value,
                    option.optionId
                )

                const tooltipText = isSelected
                    ? `${MANUFACTURER_OPTION} selected`
                    : MANUFACTURER_OPTION

                return (
                    <Option
                        key={option.optionId}
                        value={option.value ?? ''}
                        optionId={option.optionId ?? ''}
                        propertyName={propertyName}
                        subPropertyName={subPropertyName}
                        isSelected={isSelected}
                        isPremiumPlus={option.isPremiumPlus}
                        tooltipText={tooltipText}
                        disabled
                    />
                )
            }
        )
    }

    const noop = useCallback(() => {}, [])

    return (
        <span className={classes.premPlusContainer}>
            {!isManufacturerOptionsReadOnly ? (
                <>
                    <DndProperties
                        isAddActive={isAddActive}
                        setIsAddActive={setIsAddActive}
                        propertyName={propertyName}
                        subPropertyName={subPropertyName}
                        premiumOptions={premiumOptions}
                        determineSelected={determineSelected}
                        onDragEnd={handleDragEnd}
                        onRemoveClick={handleRemoveClick}
                        onSelectOption={handleSetSelected}
                        handleInsertPremiumOption={insertPremiumOption}
                        onOptionBlur={onOptionBlur}
                        onUOMBlur={onUOMBlur}
                    />
                    <IconButton
                        className={classes.addPremiumPlusProperty}
                        onClick={isAddActive ? noop : handleAddPremPlusProperty}
                        data-testid="addButton"
                    >
                        <AddOutlined className={classes.addIcon} />
                    </IconButton>
                </>
            ) : (
                viewOnlyPremiumOptions()
            )}

            {isRemoveLoading && (
                <Backdrop className={classes.backdrop} open={isRemoveLoading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
        </span>
    )
}

export default MSPremiumProperties
